import React from 'react';
import { CheckCircle, Clock, XCircle, Ban, Info } from 'lucide-react';

interface BetStatusExplainerProps {
  className?: string;
}

export const BetStatusExplainer: React.FC<BetStatusExplainerProps> = ({ className }) => {
  return (
    <details className={`bg-white rounded-lg shadow-md overflow-hidden group ${className ?? ''}`}>
      <summary className="px-6 py-4 border-b border-gray-200 flex items-center cursor-pointer list-none [&::-webkit-details-marker]:hidden">
        <Info className="w-5 h-5 text-blue-600 mr-2 shrink-0" />
        <span className="text-lg font-semibold flex-1">What do trade statuses mean?</span>
        <span className="text-sm text-gray-500 font-normal ml-2 group-open:hidden">Tap to expand</span>
        <span className="text-sm text-gray-500 font-normal ml-2 hidden group-open:inline">Tap to collapse</span>
      </summary>

      <div className="p-6 space-y-4 border-t border-gray-100">
        <p className="text-gray-600 mb-4">
          Your trades can have different statuses depending on their current state. Here's what each status means:
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center mb-2">
              <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2">
                <Clock className="w-4 h-4 mr-1" />
                <span>Active</span>
              </div>
            </div>
            <p className="text-gray-600 text-sm">
              These are open trades that can still be cancelled if the market is active. Your funds are locked until the market resolves or you cancel the trade.
            </p>
          </div>
          
          <div className="p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center mb-2">
              <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mr-2">
                <CheckCircle className="w-4 h-4 mr-1" />
                <span>Won</span>
              </div>
            </div>
            <p className="text-gray-600 text-sm">
              These trades have been resolved in your favor. Your winnings have been added to your wallet balance.
            </p>
          </div>
          
          <div className="p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center mb-2">
              <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 mr-2">
                <XCircle className="w-4 h-4 mr-1" />
                <span>Lost</span>
              </div>
            </div>
            <p className="text-gray-600 text-sm">
              These trades have been resolved against your favor. Your trade amount was not returned.
            </p>
          </div>
          
          <div className="p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center mb-2">
              <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mr-2">
                <Ban className="w-4 h-4 mr-1" />
                <span>Cancelled</span>
              </div>
            </div>
            <p className="text-gray-600 text-sm">
              These trades were cancelled by you or by an admin. Only the amount after service fees is refunded to your wallet (service fees are not refunded).
            </p>
          </div>
        </div>
        
        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="font-medium text-blue-800 mb-2">Managing Your Trades</h4>
          <ul className="list-disc pl-5 text-sm text-blue-700 space-y-1">
            <li>Only active trades on active markets can be cancelled</li>
            <li>When you cancel a trade, you get back only the amount after service fees (typically 92% of your original trade)</li>
            <li>After a market resolves, trades cannot be cancelled</li>
            <li>Cancelled trades don't appear in your open positions list</li>
          </ul>
        </div>
      </div>
    </details>
  );
}; 